import React from 'react';
import { CheckInResponse } from '../../../types';

interface CheckInsStatsProps {
  checkIns: CheckInResponse[];
  isLoading: boolean;
  showSpaceBreakdown?: boolean; // Show space breakdown when filtering "All Locations"
}

const CheckInsStats: React.FC<CheckInsStatsProps> = ({
  checkIns,
  isLoading,
  showSpaceBreakdown = false,
}) => {
  // Calculate stats from check-ins data
  const totalCheckIns = checkIns.length;
  
  const uniqueUsers = new Set(checkIns.map(ci => ci.user_id)).size;
  
  const membershipCount = checkIns.filter(ci => ci.used_quota_type === 'membership day').length;
  const dayPassCount = checkIns.filter(ci => ci.used_quota_type === 'day pass').length;
  
  // Calculate breakdown by space
  const spaceBreakdown = checkIns.reduce((acc, ci) => {
    const spaceName = ci.space?.name || 'Unknown';
    acc[spaceName] = (acc[spaceName] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  if (isLoading) {
    return (
      <div className="stats-cards">
        <div className="stat-card">
          <div className="stat-loading">Loading stats...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="stats-cards">
      <div className="stat-card">
        <div className="stat-label">Total Check-Ins</div>
        <div className="stat-value">{totalCheckIns}</div>
      </div>

      <div className="stat-card">
        <div className="stat-label">Unique Users</div>
        <div className="stat-value">{uniqueUsers}</div>
      </div>

      <div className="stat-card">
        <div className="stat-label">Membership</div>
        <div className="stat-value">{membershipCount}</div>
        <div className="stat-subtext">
          {totalCheckIns > 0 ? Math.round((membershipCount / totalCheckIns) * 100) : 0}%
        </div>
      </div>

      <div className="stat-card">
        <div className="stat-label">Day Pass</div>
        <div className="stat-value">{dayPassCount}</div>
        <div className="stat-subtext">
          {totalCheckIns > 0 ? Math.round((dayPassCount / totalCheckIns) * 100) : 0}%
        </div>
      </div>

      {showSpaceBreakdown && Object.keys(spaceBreakdown).length > 1 && (
        <div className="stat-card stat-card-wide">
          <div className="stat-label">By Location</div>
          <div className="space-breakdown">
            {Object.entries(spaceBreakdown)
              .sort((a, b) => b[1] - a[1]) // Sort by count descending
              .map(([spaceName, count]) => (
                <div key={spaceName} className="space-breakdown-item">
                  <span className="space-name">{spaceName}</span>
                  <span className="space-count">{count}</span>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckInsStats;
